import {type Node, type Edge, applyNodeChanges, applyEdgeChanges} from 'reactflow';
import {assign, fromPromise, setup} from 'xstate';

import {EntityMapEntityNodeOrInaccessibleInfoNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';

import {fetchEntityMapGraph} from './EntityMapDAO';
import {entityMapEntityNodeToReactFlowNode, entityMapEdgeNodeToReactFlowEdge} from './EntityMapUtils';

interface EntityMapInput {
  entityId: string;
}

interface EntityMapContext {
  entityId: string;
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
  graphEdgesById: Map<string, Edge>;
}

/**
 * XState state machine for loading and managing the entity map graph for a given entityId.
 * Handles loading, error, and idle states, and stores graphNodesById and graphEdgesById in context.
 *
 * The idle state handles onNodesChange and onEdgesChange events, which can be sent from the view using actorRef.send().
 */
export const EntityMapMachine = setup({
  types: {
    input: {} as EntityMapInput,
    context: {} as EntityMapContext,
  },
  actors: {
    loadInitialGraph: fromPromise<
      {
        graphNodesById: Map<string, Node>;
        graphEdgesById: Map<string, Edge>;
      },
      {entityId: string}
    >(async ({input}) => {
      const {entities, edges} = await fetchEntityMapGraph(input.entityId, 3);
      const graphNodesById = new Map(
        entities.map((entity) => entityMapEntityNodeToReactFlowNode(entity)).map((node) => [node.id, node])
      );
      const graphEdgesById = new Map(edges.map((edge) => [edge.id, entityMapEdgeNodeToReactFlowEdge(edge)]));
      return {graphNodesById, graphEdgesById};
    }),
  },
  actions: {
    layout: assign(({context}) => {
      const nodes = [...context.graphNodesById.values()];
      const edges = [...context.graphEdgesById.values()];

      const {nodes: layoutNodes, edges: layoutEdges} = layoutFlowchart({nodes, edges});
      const layoutedGraphNodesById = new Map(layoutNodes.map((node) => [node.id, node]));
      const layoutedGraphEdgesById = new Map(layoutEdges.map((edge) => [edge.id, edge]));

      return {
        graphNodesById: layoutedGraphNodesById,
        graphEdgesById: layoutedGraphEdgesById,
      };
    }),
    onNodesChange: assign(({context, event}) => {
      const nodes: Array<Node> = [...context.graphNodesById.values()];
      const updatedNodes = applyNodeChanges(event.changes, nodes);
      return {
        graphNodesById: new Map(updatedNodes.map((node) => [node.id, node])),
      };
    }),
    onEdgesChange: assign(({context, event}) => {
      const edges: Array<Edge> = [];
      const updatedEdges = applyEdgeChanges(event.changes, edges);
      return {
        graphEdgesById: new Map(updatedEdges.map((edge) => [edge.id, edge])),
      };
    }),
  },
}).createMachine({
  context: ({input}) => ({
    entityId: input.entityId,
    graphNodesById: new Map(),
    graphEdgesById: new Map(),
  }),
  initial: 'loading',
  states: {
    loading: {
      invoke: {
        input: ({context}) => ({entityId: context.entityId}),
        src: 'loadInitialGraph',
        onError: {
          target: 'error',
        },
        onDone: {
          target: 'idle',
          actions: [
            assign(({event}) => ({
              graphNodesById: event.output.graphNodesById,
              graphEdgesById: event.output.graphEdgesById,
            })),
            'layout',
          ],
        },
      },
    },
    idle: {
      on: {
        onNodesChange: {
          actions: ['onNodesChange', 'layout'],
        },
        onEdgesChange: {
          actions: ['onEdgesChange', 'layout'],
        },
      },
    },
    error: {},
  },
});
